import { useCallback, useState } from "react";
import { queryClient } from "queryClient";
import * as Sentry from "@sentry/react";
import { ACTIVE_USER_ID_KEY } from "@constants";
import { safeLocalStorage, safeSessionStorage } from "@helpers/storage";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { UserEntitiesResponse } from "@common/types/entityContext";
import { Workspace } from "@common/types/workspace";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import {
    getActiveOrganizationKeyForUserId,
    getActiveWorkspaceKeyForUserId,
    useScope,
} from "@state/scopeContext.lib";
import { getOrganization, organizationQueryKeys } from "@api/organization.api";
import { Organization } from "@common/types/organization";
import { fetchWorkspacesList } from "@api/workspace.api";
import {
    getUserEntities,
    userStatusQueryKey,
    getUserStatus,
} from "@api/user.api";
import { hasAccessToken } from "@lib/auth0";
import {
    UserStatusResponseSchema,
    UserSchema,
} from "@common/schemas/user.schema";
import { WorkspaceSchema } from "@common/schemas/workspace.schema";

interface SetupScopeParams {
    user: UserSchema;
    organization: Organization | null;
    workspaces: WorkspaceSchema[];
}

declare global {
    interface Window {
        /** Google Tag Manager data layer. */
        dataLayer?: Array<Record<string, unknown>>;
    }
}

export function useAppInitializer() {
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const { initializeScope } = useScope();

    const handleInitSuccess = useCallback(
        (
            userStatus: UserStatusResponseSchema,
            organization: Organization | null,
        ) => {
            const user = userStatus.user;

            window.dataLayer?.push({
                event: "userFetched",
                userId: user.id,
            });

            queryClient.setQueryData([userStatusQueryKey], userStatus);
            if (organization) {
                queryClient.setQueryData(
                    organizationQueryKeys.get(),
                    organization,
                );
            }

            Sentry.setUser({
                email: user.email ?? undefined,
                id: user.id.toString(),
            });
            safeLocalStorage.setItem(ACTIVE_USER_ID_KEY, user.id.toString());
        },
        [],
    );

    const handleInitFail = useCallback(() => {
        queryClient.setQueryData([userStatusQueryKey], undefined);
        safeLocalStorage.removeItem(ACTIVE_USER_ID_KEY);
        Sentry.getCurrentScope().setUser(null);
    }, []);

    const activate = useCallback(
        async (
            workspace: Pick<Workspace, "id"> | null,
            organization: Organization | null,
        ) => {
            initializeScope({
                organizationId: organization?.id ?? null,
                workspaceId: workspace?.id ?? null,
                source: OrganizationMemberClientAccessSource.HYDRATE,
            });

            if (!workspace) {
                return;
            }

            const entities = await getUserEntities(workspace.id).catch(
                () => undefined,
            );

            if (entities) {
                queryClient.setQueryData(
                    getEntitiesAccountsQueryKey(workspace.id),
                    (current: UserEntitiesResponse | undefined) => ({
                        ...(current ?? {}),
                        entities: entities.entities,
                    }),
                );
            }
        },
        [initializeScope],
    );

    const setupScope = useCallback(
        ({ user, organization, workspaces }: SetupScopeParams) => {
            const organizationForWorkspace = (workspace: WorkspaceSchema) => {
                if (!organization) {
                    return null;
                }

                if (!workspace.organizationIds.length) {
                    return null;
                }

                const isAssignedToOrganization = workspace.organizationIds.some(
                    (id) => id === organization.id,
                );

                if (!isAssignedToOrganization) {
                    return null;
                }

                return organization;
            };

            // 1. URL params take highest precedence — enables deep-linking and
            //    cross-tab workspace opening via ?workspaceId= / ?organizationId=.
            const urlParams = new URLSearchParams(window.location.search);
            const urlWorkspaceId = urlParams.get("workspaceId");
            const urlOrganizationId = urlParams.get("organizationId");

            if (urlWorkspaceId) {
                const urlWorkspace = workspaces.find(
                    (w) => w.id === urlWorkspaceId,
                );
                if (urlWorkspace) {
                    activate(
                        urlWorkspace,
                        organizationForWorkspace(urlWorkspace),
                    );
                    return;
                }
            }

            if (urlOrganizationId && organization?.id === urlOrganizationId) {
                activate(null, organization);
                return;
            }

            // 2. sessionStorage — tab's own saved scope. Survives hard refresh
            //    without sharing across tabs (unlike localStorage).
            const sessionWorkspaceId = safeSessionStorage.getItem(
                getActiveWorkspaceKeyForUserId(user.id),
            );
            const sessionWorkspace = sessionWorkspaceId
                ? workspaces.find((w) => w.id === sessionWorkspaceId)
                : null;

            if (sessionWorkspace) {
                activate(
                    sessionWorkspace,
                    organizationForWorkspace(sessionWorkspace),
                );
                return;
            }

            const sessionOrganizationId = safeSessionStorage.getItem(
                getActiveOrganizationKeyForUserId(user.id),
            );
            if (
                sessionOrganizationId &&
                organization?.id === sessionOrganizationId
            ) {
                activate(null, organization);
                return;
            }

            // 3. localStorage last-used seed — for brand-new tabs that have no
            //    sessionStorage scope and no URL hint yet.
            const storedWorkspaceId = safeLocalStorage.getItem(
                getActiveWorkspaceKeyForUserId(user.id),
            );

            const storedWorkspace = storedWorkspaceId
                ? workspaces.find(
                      (workspace) => workspace.id === storedWorkspaceId,
                  )
                : null;

            if (storedWorkspace) {
                activate(
                    storedWorkspace,
                    organizationForWorkspace(storedWorkspace),
                );

                return;
            }

            const storedOrganizationId = safeLocalStorage.getItem(
                getActiveOrganizationKeyForUserId(user.id),
            );

            const storedOrganization =
                storedOrganizationId &&
                organization?.id === storedOrganizationId
                    ? organization
                    : null;

            if (storedOrganization) {
                activate(null, storedOrganization);

                return;
            }

            if (workspaces.length === 1) {
                const firstWorkspaceDetails = workspaces[0];

                activate(
                    firstWorkspaceDetails,
                    organizationForWorkspace(firstWorkspaceDetails),
                );

                return;
            }

            // in organization, but no workspace access
            if (workspaces.length === 0 && organization) {
                activate(null, organization);
                return;
            }

            if (organization) {
                activate(null, organization);
                return;
            }

            if (user.onboardingCompletedAt) {
                activate(null, null);

                return;
            }

            const firstWorkspaceDetails = workspaces[0];

            activate(
                firstWorkspaceDetails,
                organizationForWorkspace(firstWorkspaceDetails),
            );
        },
        [activate],
    );

    const initialize = useCallback(async () => {
        const loadInitialState = async () => {
            if (!hasAccessToken()) {
                handleInitFail();
                return;
            }

            const [userStatus, workspaces, organization] = await Promise.all([
                getUserStatus(),
                fetchWorkspacesList(),
                getOrganization(),
            ]);

            const onboardedOrganization = organization?.onboardingCompletedAt
                ? organization
                : null;

            if (userStatus && workspaces) {
                handleInitSuccess(userStatus, onboardedOrganization);
            } else {
                handleInitFail();
                return;
            }

            setupScope({
                user: userStatus.user,
                organization: onboardedOrganization,
                workspaces,
            });
        };
        await loadInitialState().finally(() => {
            setIsInitialized(true);
        });
    }, [handleInitSuccess, handleInitFail, setupScope]);

    return {
        isInitialized,
        initialize,
    };
}
